import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import classNames from 'classnames';
import { IconButton } from 'mastodon/components/icon_button';
import { FacebookIcon } from 'mastodon/components/facebook_icon';
import { TiktokIcon } from 'mastodon/components/tiktok_icon';

import { showAlert } from 'mastodon/actions/alerts';
import { useAppDispatch } from 'mastodon/store';

import { LoadingIndicator } from 'mastodon/components/loading_indicator';
import { CloseIcon } from 'mastodon/components/icons/close';

const messages = {
  title: { id: 'account.sync', defaultMessage: 'Synchronize' },
  titleFacebook: { id: 'account.sync_facebook', defaultMessage: 'Sync Facebook' },
  titleTiktok: { id: 'account.sync_tiktok', defaultMessage: 'Sync Tiktok' },
  description: { id: 'account.description_sync', defaultMessage: 'Synchronize to access more important information' },
  sync_button: { id: 'account.sync_button', defaultMessage: 'here' },
  note: { id: 'account.note', defaultMessage: 'Posts are synchronized from the time the feature is enabled' },
  close: { id: 'bundle_modal_error.close', defaultMessage: 'Close' },
};

const SyncModal = ({ onClose, accessToken }) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();

  const [facebookStatus, setFacebookStatus] = useState(false);
  const [tiktokStatus, setTiktokStatus] = useState(false);
  const [facebookLoading, setFacebookLoading] = useState(false);
  const [tiktokLoading, setTiktokLoading] = useState(false);

  const handleSyncSettings = async (newState) => {
    await fetch('/api/v1/sync/settings', {
      keepalive: true,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(newState),
    });
  };

  const handleToggle = async (syncType) => {
    if (syncType === 'facebook') {
      const newValue = !facebookStatus;
      setFacebookStatus(newValue);
      await handleSyncSettings({ facebook: newValue, tiktok: tiktokStatus });
    } else {
      const newValue = !tiktokStatus;
      setTiktokStatus(newValue);
      await handleSyncSettings({ facebook: facebookStatus, tiktok: newValue });
    }
  };

  const handleSyncDataTiktok = () => {
    window.location.href = '/auth/tiktok';
  };

  const handleSyncDataFacebook = () => {
    window.location.href = '/auth/facebook';
  };

  useEffect(() => {
    const fetchSyncSettings = async () => {
      try {
        const response = await fetch('/api/v1/sync/settings', {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
        if (response.ok) {
          const data = await response.json();
          setFacebookStatus(data.facebook);
          setTiktokStatus(data.tiktok);
        }
      } catch (error) {
        console.error('Failed to fetch sync settings:', error);
      } finally {
        setFacebookLoading(false);
        setTiktokLoading(false);
      }
    };
  
    if (accessToken) {
      setFacebookLoading(true);
      setTiktokLoading(true);
      fetchSyncSettings();
    }
  }, [accessToken]);

  return (
    <div className={classNames('modal-root__modal', 'sync-modal')}>
      <div className='sync-modal-container'>
        <div className='sync-modal__header'>
          <h3>{intl.formatMessage(messages.title)}</h3>
          <IconButton className='sync-modal__close' title={intl.formatMessage(messages.close)} icon='close' iconComponent={CloseIcon} onClick={onClose} size={20} />
        </div>
        <div className='sync-modal__content'>
          <div className='sync-modal__item'>
            <FacebookIcon />
            <div className='sync-modal__item__content'>
              <h3>{intl.formatMessage(messages.titleFacebook)}</h3>
              <p
                style={{
                  opacity: facebookStatus ? 1 : 0.5,
                  pointerEvents: facebookStatus ? 'auto' : 'none',
                }}
              >
               {intl.formatMessage(messages.description)}
               <strong><u onClick={handleSyncDataFacebook}> {intl.formatMessage(messages.sync_button)}</u></strong>
              </p>
            </div>
            <label className='sync-modal__toggle'>
              {facebookLoading ? (
                <LoadingIndicator size={24} />
              ) : (
                <>
                  <input
                    type='checkbox'
                    checked={facebookStatus}
                    onChange={() => handleToggle('facebook')}
                  />
                  <span className='sync-modal__toggle-slider' />
                </>
              )}
            </label>
          </div>
          <div className='sync-modal__item'>
            <TiktokIcon />
            <div className='sync-modal__item__content'>
              <h3>{intl.formatMessage(messages.titleTiktok)}</h3>
              <p
                style={{
                  opacity: tiktokStatus ? 1 : 0.5,
                  pointerEvents: tiktokStatus ? 'auto' : 'none',
                }}
              >
                {intl.formatMessage(messages.description)}
                <strong><u onClick={handleSyncDataTiktok}> {intl.formatMessage(messages.sync_button)}</u></strong>
              </p>
            </div>
            <label className='sync-modal__toggle'>
              {tiktokLoading ? (
                <LoadingIndicator size={24} />
              ) : (
                <>
                  <input
                    type='checkbox'
                    checked={tiktokStatus}
                    onChange={() => handleToggle('tiktok')}
                  />
                  <span className='sync-modal__toggle-slider' />
                </>
              )}
            </label>
          </div>
          <span className="sync-modal__note">{intl.formatMessage(messages.note)}</span>
        </div>
      </div>
    </div>
  );
};

SyncModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  accessToken: PropTypes.string.isRequired,
  facebookSync: PropTypes.bool.isRequired,
  tiktokSync: PropTypes.bool.isRequired,
};

export default SyncModal;